/**
 * BonsTab - Bons de commande & factures en attente
 * Deux sous-onglets : Factures (bons en attente) et MANAGER GENERAL.
 * Tous les handlers et états sont gérés par CaissePage.jsx et passés en props.
 */
import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Printer, UserCircle, AlertTriangle, Edit2, CheckCircle, Eye,
  Trash2, Wine, Gamepad2, FileText, CreditCard, ChefHat, Loader2
} from 'lucide-react';
import SalaryCreditsBanner from './SalaryCreditsBanner';
import DGGroupedTab from './DGGroupedTab';
import CreditOrdersGroupedTab from './CreditOrdersGroupedTab';
import { PRODUCTION_LABELS, requiredProductionBons, missingProductionBons } from '../utils/productionBons';

const formatPrice = (p) => new Intl.NumberFormat('fr-FR').format(p || 0);
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BonsTab = ({
  currentUser,
  invoices = [],
  products = [],
  cancellationRequests = [],
  modificationRequests = [],
  filterDate,
  setFilterDate,
  // handlers
  approveCancellationRequest,
  rejectCancellationRequest,
  approveModificationRequest,
  rejectModificationRequest,
  printKitchenOrder,
  printBarOrder,
  printGamesOrder,
  setViewInvoice,
  startEditingInvoice,
  requestModification,
  requestCancellation,
  validateInvoice,
  deleteInvoice,
}) => {
  const isAdmin = currentUser?.role === 'admin';
  const isManager = currentUser?.role === 'manager';
  const isServer = currentUser?.role === 'server';
  const canManage = isAdmin || isManager;
  const serverName = currentUser?.full_name || currentUser?.username;
  const [regularizing, setRegularizing] = useState(false);

  const regularizeCuisine = async () => {
    setRegularizing(true);
    try {
      const r = await axios.post(`${API}/cuisine/regularize-today`, { actor_role: currentUser?.role, actor_name: serverName });
      const d = r.data || {};
      if (d.total_fixed > 0) {
        toast.success(`Cuisine régularisée : ${d.total_fixed} bon(s) — ${d.created.length} créé(s), ${d.restored.length} restauré(s), ${d.reopened.length} réactivé(s)`, { duration: 8000 });
      } else {
        toast.info(`Rien à régulariser : ${d.already_ok} bon(s) déjà visible(s) en cuisine`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de la régularisation");
    } finally {
      setRegularizing(false);
    }
  };

  const pendingInvoices = invoices.filter(i =>
    i.validation_status === 'pending' &&
    (!isServer || i.created_by === serverName)
  );

  return (
    <Tabs defaultValue="bons-factures" className="w-full" data-testid="bons-tab">
      {/* Admin-only banner: aperçu des crédits salaires du mois */}
      {isAdmin && <SalaryCreditsBanner formatPrice={formatPrice} />}
      <TabsList className="bg-slate-800/50 border-b border-slate-700 w-full justify-start mb-4">
        <TabsTrigger value="bons-factures" className="data-[state=active]:bg-orange-500 data-[state=active]:text-white">
          <Printer className="w-4 h-4 mr-2" />
          Factures
          {invoices.filter(i => i.validation_status === 'pending').length > 0 && (
            <Badge className="ml-1 bg-orange-600 text-white text-xs">
              {invoices.filter(i => i.validation_status === 'pending').length}
            </Badge>
          )}
        </TabsTrigger>
        {canManage && (
          <TabsTrigger value="bons-monsieur" className="data-[state=active]:bg-purple-500 data-[state=active]:text-white" data-testid="tab-bons-dg">
            <UserCircle className="w-4 h-4 mr-2" />
            LA DIRECTION
          </TabsTrigger>
        )}
        {canManage && (
          <TabsTrigger value="bons-credit" className="data-[state=active]:bg-pink-500 data-[state=active]:text-white" data-testid="tab-bons-credit">
            <CreditCard className="w-4 h-4 mr-2" />
            BONS À CRÉDIT
          </TabsTrigger>
        )}
      </TabsList>

      {/* Factures sub-tab */}
      <TabsContent value="bons-factures">
        <div className="space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h2 className="text-xl font-bold text-orange-400 flex items-center gap-2">
              <Printer className="w-6 h-6" />
              Factures
              <Badge className="bg-orange-500/20 text-orange-300 text-lg px-3">
                {invoices.filter(i => i.validation_status === 'pending').length}
              </Badge>
            </h2>
            <div className="flex items-center gap-2 flex-wrap">
              {canManage && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={regularizeCuisine}
                  disabled={regularizing}
                  className="border-amber-500/50 text-amber-200 hover:bg-amber-500/10 h-9"
                  title="Refaire apparaître chez le cuisinier tous les bons du jour en attente contenant des plats"
                  data-testid="regularize-cuisine-btn"
                >
                  {regularizing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <ChefHat className="w-4 h-4 mr-1" />}
                  Régulariser cuisine (jour)
                </Button>
              )}
              <Input
                type="date"
                value={filterDate}
                onChange={(e) => setFilterDate(e.target.value)}
                className="bg-slate-800/50 border-slate-700 text-white w-auto"
                data-testid="bons-filter-date"
              />
            </div>
          </div>

          {/* Cancellation requests (admin) */}
          {isAdmin && cancellationRequests.length > 0 && (
            <Card className="bg-gradient-to-br from-red-900/30 to-orange-900/20 border-red-500/50" data-testid="cancellation-requests-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-red-400 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5" />
                  DEMANDES D'ANNULATION
                  <Badge className="bg-red-500/30 text-red-300 ml-2">{cancellationRequests.length}</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {cancellationRequests.map(req => {
                  const invoice = invoices.find(inv => inv.id === req.invoice_id);
                  const itemsCount = invoice?.items?.length || 0;
                  const amount = invoice?.total_ttc ?? invoice?.total ?? 0;
                  let timeLabel = "";
                  try {
                    if (req.created_at) {
                      const d = new Date(req.created_at);
                      timeLabel = d.toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" });
                    }
                  } catch (_) {}
                  return (
                    <div
                      key={req.id}
                      className="bg-red-900/20 rounded-lg p-3 border border-red-500/30 space-y-2"
                      data-testid={`cancellation-req-${req.id}`}
                    >
                      <div className="flex items-start justify-between gap-2 flex-wrap">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="text-white font-bold text-base">{req.invoice_number}</p>
                            {amount > 0 && (
                              <Badge className="bg-red-500/30 text-red-200 border border-red-500/40 text-xs">
                                {formatPrice(amount)} F
                              </Badge>
                            )}
                            {itemsCount > 0 && (
                              <Badge className="bg-slate-700/60 text-slate-200 text-[10px]">
                                {itemsCount} article{itemsCount > 1 ? "s" : ""}
                              </Badge>
                            )}
                          </div>
                          <p className="text-slate-300 text-xs mt-1">
                            <strong className="text-white">{req.requested_by || "—"}</strong>
                            {timeLabel && (
                              <span className="text-slate-400"> · demande envoyée le {timeLabel}</span>
                            )}
                          </p>
                          <p className="text-slate-300 text-sm mt-1">
                            <span className="text-slate-400">Motif :</span>{" "}
                            <em className="text-red-200">{req.reason || "(non précisé)"}</em>
                          </p>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setViewInvoice && invoice && setViewInvoice(invoice)}
                            disabled={!invoice}
                            className="border-amber-500/50 text-amber-300 hover:bg-amber-500/10"
                            data-testid={`cancellation-view-${req.id}`}
                            title={invoice ? "Voir le détail de la facture (articles, montants)" : "Facture introuvable"}
                          >
                            <Eye className="w-4 h-4 mr-1" /> Voir détail
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => approveCancellationRequest(req.id)}
                            className="bg-red-600 hover:bg-red-700"
                            data-testid={`cancellation-approve-${req.id}`}
                          >
                            <CheckCircle className="w-4 h-4 mr-1" /> Annuler Facture
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => rejectCancellationRequest(req.id)}
                            className="border-slate-600 text-slate-400 hover:bg-slate-700"
                            data-testid={`cancellation-reject-${req.id}`}
                          >
                            Refuser
                          </Button>
                        </div>
                      </div>

                      {/* Mini-récap des articles pour décision rapide */}
                      {itemsCount > 0 && (
                        <details className="bg-slate-900/40 border border-slate-700/40 rounded p-2 group">
                          <summary className="text-[11px] text-slate-300 cursor-pointer hover:text-amber-300 select-none">
                            Aperçu des articles ({itemsCount}) ▾
                          </summary>
                          <ul className="text-[11px] text-slate-300 mt-2 space-y-0.5 max-h-40 overflow-y-auto">
                            {invoice.items.map((it, i) => (
                              <li key={i} className="flex justify-between">
                                <span className="truncate">— {it.name}</span>
                                <span className="text-slate-400 whitespace-nowrap">
                                  {it.quantity} × {formatPrice(it.price)} = <strong className="text-slate-200">{formatPrice((it.quantity || 0) * (it.price || 0))} F</strong>
                                </span>
                              </li>
                            ))}
                          </ul>
                        </details>
                      )}
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          )}

          {/* Modification requests (admin/manager) */}
          {canManage && modificationRequests.length > 0 && (
            <Card className="bg-gradient-to-br from-blue-900/30 to-indigo-900/20 border-blue-500/50">
              <CardHeader className="pb-2">
                <CardTitle className="text-blue-400 flex items-center gap-2">
                  <Edit2 className="w-5 h-5" />
                  DEMANDES DE MODIFICATION
                  <Badge className="bg-blue-500/30 text-blue-300 ml-2">{modificationRequests.length}</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {modificationRequests.map(req => (
                  <div key={req.id} className="flex items-center justify-between gap-2 bg-blue-900/20 rounded-lg p-3 border border-blue-500/30">
                    <div className="flex-1">
                      <p className="text-white font-medium">{req.invoice_number}</p>
                      <p className="text-slate-400 text-sm">Demandé par: {req.requested_by} • Motif: {req.reason}</p>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" onClick={() => approveModificationRequest(req.id)} className="bg-blue-600 hover:bg-blue-700">
                        <CheckCircle className="w-4 h-4 mr-1" />Autoriser
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => rejectModificationRequest(req.id)} className="border-slate-600 text-slate-400">
                        Refuser
                      </Button>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Workflow tip */}
          {canManage && (
            <div className="mb-4 p-3 bg-orange-900/20 border border-orange-500/30 rounded-lg">
              <p className="text-orange-300 text-sm flex items-center gap-2">
                <Printer className="w-4 h-4" />
                <span><strong>Workflow:</strong> 1. Imprimer les bons (Cuisine/Bar/Jeux) → 2. Cliquer sur "Bon-Client" pour transformer le bon en facture définitive</span>
              </p>
            </div>
          )}

          {/* Pending invoices list */}
          {pendingInvoices.length === 0 ? (
            <Card className="bg-slate-800/30 border-slate-700">
              <CardContent className="py-12 text-center">
                <Printer className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                <p className="text-slate-500">Aucun facture en attente</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-3">
              {pendingInvoices.map(invoice => (
                <Card key={invoice.id} className="bg-gradient-to-br from-orange-900/20 to-amber-800/10 border-orange-500/30">
                  <CardContent className="p-4">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                      <div className="flex-1 cursor-pointer min-w-0" onClick={() => setViewInvoice(invoice)}>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-white font-bold text-lg">{invoice.invoice_number}</span>
                          {invoice.bon_number && (
                            <Badge className="bg-orange-500/30 text-orange-300 border border-orange-500/40 text-xs font-bold">
                              {invoice.bon_number}
                            </Badge>
                          )}
                          {invoice.table_number && (
                            <Badge className="bg-amber-500/30 text-amber-300">Table {invoice.table_number}</Badge>
                          )}
                          {invoice.modification_allowed && (
                            <Badge className="bg-green-500/20 text-green-400 text-xs">✓ Modif. autorisée</Badge>
                          )}
                          {invoice.modified_at && (
                            <Badge className="bg-amber-500/20 text-amber-300 text-xs" data-testid="invoice-modified-badge" title={`Modifié le ${new Date(invoice.modified_at).toLocaleString('fr-FR')}`}>
                              MODIFIÉ · {new Date(invoice.modified_at).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })}
                              {invoice.modified_by ? ` par ${invoice.modified_by}` : ""}
                            </Badge>
                          )}
                        </div>
                        {canManage && requiredProductionBons(invoice.items).length > 0 && (
                          <div className="flex items-center gap-1 flex-wrap mt-1" data-testid={`production-bons-status-${invoice.id}`}>
                            {requiredProductionBons(invoice.items).map((d) => {
                              const stamp = invoice.production_bons?.[d];
                              return stamp ? (
                                <Badge key={d} className="bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 text-[10px]" title={`Imprimé le ${new Date(stamp.printed_at).toLocaleString('fr-FR')} par ${stamp.printed_by}`} data-testid={`bon-printed-${d}`}>
                                  ✓ {PRODUCTION_LABELS[d]} envoyé
                                </Badge>
                              ) : (
                                <Badge key={d} className="bg-red-500/20 text-red-300 border border-red-500/40 text-[10px]" data-testid={`bon-missing-${d}`}>
                                  ⚠ {PRODUCTION_LABELS[d]} à imprimer
                                </Badge>
                              );
                            })}
                          </div>
                        )}
                        <p className="text-slate-400 text-sm">
                          {invoice.customer_name} • <span className="text-amber-400 font-bold">{formatPrice(invoice.total)} F</span>
                        </p>
                        <p className="text-slate-500 text-xs">
                          Par: {invoice.created_by} • {new Date(invoice.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                        </p>
                        <div className="flex flex-wrap gap-1 mt-2">
                          {invoice.items?.slice(0, 4).map((item, idx) => (
                            <span key={idx} className="text-xs bg-slate-700/50 text-slate-300 px-2 py-0.5 rounded">
                              {item.quantity}x {item.name}
                            </span>
                          ))}
                          {invoice.items?.length > 4 && (
                            <span className="text-xs text-slate-500">+{invoice.items.length - 4} autres</span>
                          )}
                        </div>
                      </div>

                      <div className="flex gap-1 shrink-0 flex-wrap">
                        {canManage && (
                          <>
                            <Button size="sm" variant="outline" onClick={() => printKitchenOrder(invoice)} className="border-green-500/50 text-green-400 hover:bg-green-500/20" title="Cuisine">
                              <Printer className="w-4 h-4 mr-1" />
                              <span className="hidden sm:inline">Cuisine</span>
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => printBarOrder(invoice)} className="border-orange-500/50 text-orange-400 hover:bg-orange-500/20" title="Bar">
                              <Wine className="w-4 h-4 mr-1" />
                              <span className="hidden sm:inline">Bar</span>
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => printGamesOrder(invoice)} className="border-blue-500/50 text-blue-400 hover:bg-blue-500/20" title="Jeux">
                              <Gamepad2 className="w-4 h-4 mr-1" />
                              <span className="hidden sm:inline">Jeux</span>
                            </Button>
                          </>
                        )}
                        <Button size="sm" variant="ghost" onClick={() => setViewInvoice(invoice)} className="text-slate-400 hover:text-white">
                          <Eye className="w-4 h-4" />
                        </Button>
                        {canManage && (
                          <Button
                            size="sm"
                            onClick={() => startEditingInvoice(invoice)}
                            className="bg-amber-600 hover:bg-amber-700 text-white"
                            title={invoice.modified_at ? `Déjà modifié le ${new Date(invoice.modified_at).toLocaleString('fr-FR')} par ${invoice.modified_by || 'Admin'}` : "Modifier cette facture (signalement automatique)"}
                            data-testid="invoice-admin-edit"
                          >
                            <Edit2 className="w-4 h-4 mr-1" />
                            Modifier
                          </Button>
                        )}
                        {isServer && invoice.created_by === serverName && (
                          invoice.modification_allowed ? (
                            <Button size="sm" onClick={() => startEditingInvoice(invoice)} className="bg-blue-600 hover:bg-blue-700 text-white">
                              <Edit2 className="w-4 h-4 mr-1" />
                              Modifier
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => requestModification(invoice)}
                              className="border-blue-500/50 text-blue-400 hover:bg-blue-500/20"
                              disabled={modificationRequests.some(r => r.invoice_id === invoice.id)}                            >
                              {modificationRequests.some(r => r.invoice_id === invoice.id) ? (
                                <span className="text-xs">Envoyé</span>
                              ) : (
                                <span className="text-xs">Demander modif.</span>
                              )}
                            </Button>
                          )
                        )}
                        {canManage && (
                          <>
                            {(() => {
                              const missing = isManager ? missingProductionBons(invoice) : [];
                              const blocked = missing.length > 0;
                              return (
                                <Button
                                  size="sm"
                                  onClick={() => validateInvoice(invoice.id)}
                                  className={blocked
                                    ? "bg-slate-700 text-slate-400 border border-red-500/40 cursor-not-allowed hover:bg-slate-700"
                                    : "bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white"}
                                  title={blocked ? `Imprimez d'abord les bons ${missing.map((d) => PRODUCTION_LABELS[d]).join(", ")}` : "Éditer le Bon-Client"}
                                  data-testid={`bon-client-btn-${invoice.id}`}
                                  data-blocked={blocked ? "true" : "false"}
                                >
                                  <FileText className="w-4 h-4 mr-1" />
                                  Bon-Client{blocked ? " 🔒" : ""}
                                </Button>
                              );
                            })()}
                            {/* Suppression directe : Admin only (24/05/2026)
                                La Responsable Op. & Log doit demander l'autorisation via "Demander annulation". */}
                            {isAdmin ? (
                              <Button size="sm" variant="ghost" onClick={() => deleteInvoice(invoice.id)} className="text-red-400 hover:text-red-300" title="Supprimer">
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            ) : (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => requestCancellation(invoice)}
                                className="border-red-500/50 text-red-400 hover:bg-red-500/20"
                                disabled={cancellationRequests.some(r => r.invoice_id === invoice.id)}
                                data-testid={`bon-request-delete-${invoice.id}`}
                                title="Demander la suppression à l'administrateur"
                              >
                                <Trash2 className="w-4 h-4 mr-1" />
                                <span className="text-xs">
                                  {cancellationRequests.some(r => r.invoice_id === invoice.id) ? "Demande envoyée" : "Demander suppression"}
                                </span>
                              </Button>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </TabsContent>

      {/* la Direction (groupé : Actives + Archivées) */}
      {canManage && (
        <TabsContent value="bons-monsieur">
          <DGGroupedTab
            currentUser={currentUser}
            formatPrice={formatPrice}
            products={products}
          />
        </TabsContent>
      )}

      {/* Bons à crédit (groupé : Employés + Responsable Op. & Log) */}
      {canManage && (
        <TabsContent value="bons-credit">
          <CreditOrdersGroupedTab
            currentUser={currentUser}
            formatPrice={formatPrice}
            products={products}
          />
        </TabsContent>
      )}
    </Tabs>
  );
};

export default BonsTab;
